import { SidebarProvider, useSidebar } from "units/dashboard/context/SidebarProvider";
import Header from "units/dashboard/components/Header";
import Footer from "units/dashboard/components/Footer";
import UserSidebar from "./components/UserSidebar";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  getWelcomeMessage,
  patchWelcomeMessage,
} from "./services/welcomeMessage";
import {
  MessageCircle,
  RotateCcw,
  Save,
  CheckCircle2,
  Bot,
} from "lucide-react";
import { showToast } from "toast/toastHelper";
import { useTranslation } from "react-i18next";

const WelcomeMessageInner = () => {
  const { t } = useTranslation(["user"]);

  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState(false);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const username = useSelector((state) => state.userInfo.name);
  const [saving, setSaving] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");

  const loadWelcomeMessage = async () => {
    try {
      setLoading(true);
      const res = await getWelcomeMessage(accessToken);
      setWelcomeMessage(res?.data?.welcomeMessage || "");
    } catch (error) {
      console.error("Error loading welcome message:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      loadWelcomeMessage();
    }
  }, [accessToken]);

  /* ---------- بستن سایدبار موبایل هنگام resize به دسکتاپ ---------- */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && isSidebarOpen) {
        toggleSidebar();
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen, toggleSidebar]);

  const defaultMessage = t("welcomeMessage.defaultMessageTemplate", { name: username });

  const effectiveMessage = welcomeMessage.trim() || defaultMessage;

  const isCustom = welcomeMessage.trim().length > 0;

  const maxLength = 600;

  const handleMessageChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxLength) {
      setWelcomeMessage(value);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await patchWelcomeMessage(welcomeMessage.trim(), accessToken);
      if (res?.ok) {
        showToast(t("welcomeMessage.successToast"), "success");
      }
      if (accessToken) {
        loadWelcomeMessage();
      }
    } catch (error) {
      console.error("Error updating welcome message:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      const res = await patchWelcomeMessage(defaultMessage.trim(), accessToken);
      if (res?.ok) {
        showToast(t("welcomeMessage.successToast"), "success");
      }
      if (accessToken) {
        loadWelcomeMessage();
      }
    } catch (error) {
      console.error("Error updating welcome message:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-white to-teal-50/40 dark:!from-slate-950 dark:!via-slate-900 dark:!to-slate-950">

      {/* ================= Header ================= */}
      <nav className="shrink-0 z-30 h-[70px] flex items-center border-b border-teal-500/20 dark:!border-teal-400/15 bg-white/85 dark:!bg-slate-900/85 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.35)] print:hidden">
        <Header />
      </nav>

      {/* ================= Body ================= */}
      <div className="flex-1 min-h-0 relative">

        {/* ---------- Mobile backdrop ---------- */}
        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="
              lg:hidden fixed top-[70px] inset-x-0 bottom-0 z-30
              bg-slate-900/50 dark:!bg-black/60
              backdrop-blur-sm
              transition-opacity duration-300
            "
            aria-hidden="true"
          />
        )}

        {/* ---------- Sidebar ---------- */}
        <aside
          className={`
            fixed top-[70px] bottom-0
            rtl:right-0 ltr:left-0
            w-64
            z-40

            transform transition-transform duration-300 ease-out
            ${isSidebarOpen
              ? "translate-x-0"
              : "rtl:translate-x-full ltr:-translate-x-full"
            }
            lg:!translate-x-0

            rtl:border-l ltr:border-r
            border-teal-500/10 dark:!border-teal-400/10
            bg-gradient-to-b from-white/95 via-teal-50/60 to-white/95
            dark:!from-slate-900/95 dark:!via-slate-900/98 dark:!to-slate-900/95
            backdrop-blur-xl
            shadow-[0_0_40px_-10px_rgba(15,118,110,0.4)] lg:shadow-none
            print:hidden
            overflow-hidden
          `}
        >
          <UserSidebar />
        </aside>

        {/* ---------- Main ---------- */}
        <main className="h-full flex flex-col lg:rtl:pr-64 lg:ltr:pl-64 min-w-0">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar">

            {loading ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-100 border-t-teal-600" />
              </div>
            ) : (
              <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10">
                <div className="max-w-7xl mx-auto">

                  {/* Page Header */}
                  <div className="mb-6 sm:mb-8">
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-teal-100 to-teal-50 dark:!from-teal-900/40 dark:!to-teal-900/20 flex items-center justify-center flex-shrink-0 ring-1 ring-teal-200/60 dark:!ring-teal-800/50">
                        <MessageCircle
                          size={20}
                          className="text-teal-700 dark:!text-teal-300 sm:w-[23px] sm:h-[23px]"
                        />
                      </div>

                      <div className="min-w-0">
                        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-teal-950 dark:!text-white">
                          {t("welcomeMessage.pageTitle")}
                        </h1>

                        <p className="text-xs sm:text-sm text-teal-800/70 dark:!text-zinc-400 mt-1 leading-6">
                          {t("welcomeMessage.pageSubtitle")}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Main Grid */}
                  <div className="flex flex-col sm:!flex-row justify-center items-center gap-6">

                    {/* ================= LEFT ================= */}
                    <section className="bg-white w-full sm:!w-3/5 dark:!bg-zinc-800 border border-teal-200/70 dark:!border-zinc-700 rounded-2xl p-4 lg:p-4 shadow-xl shadow-teal-900/5">

                      <div className="mb-5 sm:mb-7">
                        <h2 className="text-base sm:text-lg font-bold text-teal-950 dark:!text-white">
                          {t("welcomeMessage.settingsTitle")}
                        </h2>

                        <p className="text-xs sm:text-sm leading-6 sm:leading-7 text-teal-800/70 dark:!text-zinc-400 mt-1">
                          {t("welcomeMessage.settingsDescription")}
                        </p>
                      </div>

                      {/* Textarea */}
                      <div>
                        <label
                          htmlFor="welcomeMessage"
                          className="block text-xs sm:text-sm font-semibold text-teal-950 dark:!text-zinc-200 mb-2"
                        >
                          {t("welcomeMessage.label")}
                        </label>

                        <textarea
                          id="welcomeMessage"
                          value={welcomeMessage}
                          onChange={handleMessageChange}
                          maxLength={maxLength}
                          placeholder={t("welcomeMessage.placeholder")}
                          className="
                            w-full
                            min-h-[130px] sm:min-h-[150px]
                            resize-y
                            rounded-xl
                            border-[1.5px]
                            border-teal-100
                            dark:!border-zinc-600
                            bg-[#FBFDFC]
                            dark:!bg-zinc-900
                            text-teal-950
                            dark:!text-white
                            placeholder:text-teal-900/40
                            dark:placeholder:!text-zinc-500
                            px-3.5 sm:px-4 py-3 sm:py-3.5
                            text-sm
                            leading-7
                            outline-none
                            transition-all
                            focus:border-teal-500
                            focus:ring-4
                            focus:ring-teal-500/10
                            dark:!focus:border-teal-500
                            dark:!focus:ring-teal-500/10
                          "
                        />

                        {/* Character Count */}
                        <div className="flex flex-wrap items-center justify-between gap-2 mt-2 text-[11px] sm:text-xs text-teal-800/60 dark:!text-zinc-500">
                          <span
                            className={
                              welcomeMessage.length >= maxLength
                                ? "text-red-500 font-semibold"
                                : ""
                            }
                          >
                            {welcomeMessage.length.toLocaleString("fa-IR")} /{" "}
                            {maxLength.toLocaleString("fa-IR")}
                          </span>

                          <span className="hidden sm:inline">
                            {t("welcomeMessage.maxLengthHint")}
                          </span>
                        </div>
                      </div>

                      {/* Effective Message */}
                      <div
                        className={`
                          mt-5 sm:mt-6
                          p-3.5 sm:p-4
                          rounded-xl
                          flex
                          gap-3
                          items-start
                          border
                          ${isCustom
                            ? "bg-teal-50/70 border-teal-200/70 dark:!bg-teal-900/20 dark:!border-teal-800"
                            : "bg-amber-50/70 border-amber-200/70 dark:!bg-amber-900/20 dark:!border-amber-800"
                          }
                        `}
                      >
                        <span
                          className={`
                            w-2 h-2 rounded-full mt-2 flex-shrink-0
                            ${isCustom ? "bg-teal-600" : "bg-amber-500"}
                          `}
                        />

                        <div className="min-w-0">
                          <strong className="block text-[11px] sm:text-xs font-bold text-teal-950 dark:!text-zinc-200 mb-1">
                            {t("welcomeMessage.defaultMessageLabel")}
                          </strong>

                          <p className="text-xs sm:text-sm leading-6 sm:leading-7 text-teal-900 dark:!text-zinc-300 break-words">
                            {defaultMessage}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-between sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-3 mt-5 sm:mt-6">

                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={saving}
                          className="
                            inline-flex items-center justify-center gap-2
                            bg-teal-700 hover:bg-teal-800
                            disabled:opacity-60 disabled:cursor-not-allowed
                            active:scale-[0.98]
                            text-white px-5 sm:px-6 py-2.5 rounded-xl
                            text-xs sm:text-sm font-semibold transition-all duration-150
                            shadow-sm shadow-teal-900/20 focus:outline-none
                            focus:ring-4 focus:ring-teal-500/20
                          "
                        >
                          <Save size={16} />
                          {saving ? t("welcomeMessage.saving") : t("welcomeMessage.save")}
                        </button>

                        <button
                          type="button"
                          onClick={handleReset}
                          className="
                            inline-flex items-center justify-center gap-2
                            bg-transparent text-teal-800/80 dark:!text-zinc-400
                            border border-teal-200 dark:!border-zinc-600
                            hover:border-amber-400 hover:text-amber-700
                            dark:!hover:text-amber-400 hover:bg-amber-50/50
                            active:scale-[0.98]
                            px-4 sm:px-5 py-2.5 rounded-xl
                            text-xs sm:text-sm font-medium
                            transition-all duration-150
                            focus:outline-none focus:ring-4 focus:ring-amber-500/10
                          "
                        >
                          <RotateCcw size={15} />
                          {t("welcomeMessage.reset")}
                        </button>
                      </div>

                      {/* Info */}
                      <div className="mt-6 sm:mt-7 pt-5 border-t border-dashed border-teal-100 dark:!border-zinc-700">
                        <div className="flex items-start gap-2.5">
                          <CheckCircle2
                            size={16}
                            className="text-amber-500 mt-0.5 flex-shrink-0"
                          />
                          <p className="text-[11px] sm:text-xs leading-6 text-teal-800/70 dark:!text-zinc-400">
                            {t("welcomeMessage.infoNote")}
                          </p>
                        </div>
                      </div>
                    </section>

                    {/* ================= RIGHT ================= */}
                    <section className="flex flex-col w-full sm:!w-2/5 items-center">

                      {/* Preview Heading */}
                      <div className="w-full max-w-[360px] mb-4 sm:mb-5 text-right">
                        <div className="flex items-center gap-2 mb-1">
                          <Bot size={18} className="text-teal-700 dark:!text-teal-400" />
                          <h2 className="text-base sm:text-lg font-bold text-teal-950 dark:!text-white">
                            {t("welcomeMessage.preview.title")}
                          </h2>
                        </div>

                        <p className="text-xs sm:text-sm leading-6 text-teal-800/70 dark:!text-zinc-400">
                          {t("welcomeMessage.preview.description")}
                        </p>
                      </div>

                      {/* Phone */}
                      <div className="w-full max-w-[300px] sm:max-w-[340px] bg-gradient-to-b from-teal-950 to-teal-900 rounded-[34px] p-3 sm:p-3.5 shadow-[0_22px_44px_-20px_rgba(15,80,70,0.55)]">
                        <div className="bg-[#FBFDFC] dark:!bg-zinc-950 rounded-[22px] overflow-hidden h-[480px] sm:h-[540px] flex flex-col">

                          {/* Phone Header */}
                          <div className="bg-gradient-to-l from-teal-800 to-teal-700 text-white px-3.5 sm:px-4 py-3.5 sm:py-4 flex items-center gap-2.5">
                            <div className="w-8 h-8 sm:w-[34px] sm:h-[34px] rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center font-bold text-xs sm:text-sm text-white flex-shrink-0 ring-2 ring-amber-300/40">
                              {t("welcomeMessage.preview.assistantInitial")}
                            </div>

                            <div>
                              <strong className="block text-xs sm:text-sm font-semibold">
                                {t("welcomeMessage.preview.assistantName")}
                              </strong>
                              <span className="block text-[10px] sm:text-[11px] opacity-80 mt-0.5">
                                {t("welcomeMessage.preview.online")}
                              </span>
                            </div>
                          </div>

                          {/* Phone Body */}
                          <div className="flex-1 px-3 sm:px-3.5 py-3.5 sm:py-4 overflow-y-auto flex flex-col gap-2.5">
                            <div className="self-start text-[10px] sm:text-[11px] text-teal-800/50 dark:!text-zinc-500 px-1">
                              {t("welcomeMessage.preview.conversationStart")}
                            </div>

                            <div className="max-w-[82%] self-start bg-teal-50 dark:!bg-zinc-800 text-teal-950 dark:!text-zinc-200 rounded-[14px] rounded-bl-[4px] px-3 sm:px-3.5 py-2 sm:py-2.5 text-[12px] sm:text-[13px] leading-6 break-words border border-teal-100/70 dark:!border-zinc-700">
                              {effectiveMessage}
                            </div>
                          </div>

                          {/* Phone Footer */}
                          <div className="px-3 sm:px-3.5 pt-2.5 pb-3.5 sm:pb-4 border-t border-teal-100/60 dark:!border-zinc-800">
                            <div className="bg-teal-50/60 dark:!bg-zinc-900 rounded-full px-3.5 sm:px-4 py-2 sm:py-2.5 text-[11px] sm:text-xs text-teal-800/50 dark:!text-zinc-500">
                              {t("welcomeMessage.preview.inputPlaceholder")}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Preview Status */}
                      <div className="flex items-center gap-2 mt-4 text-[11px] sm:text-xs text-teal-800/70 dark:!text-zinc-500">
                        <span
                          className={`
                            w-2 h-2 rounded-full
                            ${isCustom ? "bg-teal-600" : "bg-amber-500"}
                          `}
                        />
                        {isCustom
                          ? t("welcomeMessage.preview.customMessage")
                          : t("welcomeMessage.preview.defaultMessage")}
                      </div>
                    </section>
                  </div>

                  {/* Footer */}
                  <div className="mt-10">
                    <Footer />
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(20, 184, 166, 0.55) transparent;
          scroll-behavior: smooth;
        }
        .custom-scrollbar::-webkit-scrollbar { width: 10px; height: 10px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; border-radius: 999px; margin: 6px 0; }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          border-radius: 999px; border: 2px solid transparent; background-clip: padding-box;
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(45,212,191,0.7), rgba(251,191,36,0.55));
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.9), rgba(45,212,191,1), rgba(251,191,36,0.9));
          box-shadow: 0 0 12px -2px rgba(20,184,166,0.7);
        }
        .dark .custom-scrollbar { scrollbar-color: rgba(251,191,36,0.45) transparent; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-image: linear-gradient(180deg, rgba(20,184,166,0.55), rgba(94,234,212,0.65), rgba(251,191,36,0.65));
        }
      `}</style>
    </div>
  );
};

const WelcomeMessage = () => (
  <SidebarProvider>
    <WelcomeMessageInner />
  </SidebarProvider>
);

export default WelcomeMessage;